import React, { useState, useMemo, useEffect } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import {
  BookOpenIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

/* ================================================================
   CONSTANTS & CONFIG
   ================================================================ */
const COURSE_FREQUENCY = {
  quarterly: { label: 'Quarterly', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20' },
  annual: { label: 'Annual', color: 'text-psg-teal', bg: 'bg-psg-teal/10' },
  biennial: { label: 'Biennial', color: 'text-violet-600', bg: 'bg-violet-50 dark:bg-violet-900/20' },
  on_commencement: { label: 'On Commencement', color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
}

const ROLE_SCOPE_LABELS = {
  all: 'All Gaming Staff',
  co: 'Compliance Officer',
  manager: 'Managers & CO',
  supervisor: 'Supervisors, CO & Cashiers',
  new_staff: 'New Starters',
}

function courseFreqMeta(frequency) {
  return COURSE_FREQUENCY[frequency] || COURSE_FREQUENCY.annual
}

function isCourseApplicable(course, staff) {
  const scope = course.roles || 'all'
  if (scope === 'all') return true
  if (scope === 'new_staff') return staff.roleKeys?.includes('new_staff')
  return staff.roleKeys?.includes(scope)
}

/** Local calendar date. Avoids UTC `toISOString()`, which is the previous day in Australia after midnight. */
function localIsoDate(date = new Date()) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** FY quarter from a calendar date (YYYY-MM-DD), not a timezone-shifted timestamp. */
function periodKeyFromYmd(value) {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2]) - 1
  let fyEndYear
  let quarter
  if (month >= 6) {
    fyEndYear = year + 1
    quarter = month >= 9 ? 'Q2' : 'Q1'
  } else {
    fyEndYear = year
    quarter = month >= 3 ? 'Q4' : 'Q3'
  }
  return `FY${String(fyEndYear).slice(-2)}_${quarter}`
}

/** Australian FY quarter from a Date. FY26 = Jul 2025–Jun 2026 (year FY ends). */
function australianFyQuarter(date = new Date()) {
  const year = date.getFullYear()
  const month = date.getMonth() // 0–11
  let fyEndYear
  let quarter
  if (month >= 6) {
    // Jul–Jun → FY ends next calendar year
    fyEndYear = year + 1
    quarter = month >= 9 ? 'Q2' : 'Q1' // Jul–Sep Q1, Oct–Dec Q2
  } else {
    fyEndYear = year
    quarter = month >= 3 ? 'Q4' : 'Q3' // Jan–Mar Q3, Apr–Jun Q4
  }
  const fy = String(fyEndYear).slice(-2)
  return {
    fyEndYear,
    fyLabel: `FY${fy}`,
    key: `FY${fy}_${quarter}`,
    quarter,
  }
}

/** Build navigator periods: previous FY + current FY (8 quarters). */
function buildTrainingPeriods(now = new Date()) {
  const { fyEndYear } = australianFyQuarter(now)
  const quarters = [
    { q: 'Q1', months: 'Jul–Sep', startMonth: 6 },
    { q: 'Q2', months: 'Oct–Dec', startMonth: 9 },
    { q: 'Q3', months: 'Jan–Mar', startMonth: 0 },
    { q: 'Q4', months: 'Apr–Jun', startMonth: 3 },
  ]
  const periods = []
  for (const endYear of [fyEndYear - 1, fyEndYear]) {
    const startYear = endYear - 1
    const fy = String(endYear).slice(-2)
    for (const { q, months } of quarters) {
      const rangeYear = q === 'Q1' || q === 'Q2' ? startYear : endYear
      periods.push({
        key: `FY${fy}_${q}`,
        label: `${q} FY${String(startYear).slice(-2)}-${fy}`,
        range: `${months} ${rangeYear}`,
      })
    }
  }
  return periods
}

function getCompletionStatus(course, staff, periodKey, completionsData) {
  if (!isCourseApplicable(course, staff)) return 'na'
  const fy = periodKey?.substring(0, 4) || australianFyQuarter().fyLabel
  const viewFyNum = parseInt(fy?.substring(2, 4) || '26', 10)
  if (course.frequency === 'quarterly') {
    const key = `${staff.id}_${course.id}_${periodKey}`
    return completionsData[key] ? 'completed' : 'not_done'
  }
  if (course.frequency === 'on_commencement') {
    const key = `${staff.id}_${course.id}`
    return completionsData[key] ? 'completed' : 'not_done'
  }
  if (course.frequency === 'annual') {
    const key = `${staff.id}_${course.id}`
    const comp = completionsData[key]
    if (!comp) return 'not_done'
    const compFY = comp.period?.substring(0, 4)
    const compFyNum = parseInt(compFY?.substring(2, 4) || '0', 10)
    if (compFY === fy) return 'completed'
    // Older FY completion → expired for this year; future FY → not yet done for this past period
    return compFyNum < viewFyNum ? 'expired' : 'not_done'
  }
  if (course.frequency === 'biennial') {
    const key = `${staff.id}_${course.id}`
    const comp = completionsData[key]
    if (!comp) return 'not_done'
    const compFY = parseInt(comp.period?.substring(2, 4) || '26', 10)
    if (compFY > viewFyNum) return 'not_done'
    return viewFyNum - compFY <= 2 ? 'completed' : 'expired'
  }
  // Unknown frequency: treat like annual so incomplete training still shows as No
  {
    const key = `${staff.id}_${course.id}`
    const comp = completionsData[key]
    if (!comp) return 'not_done'
    const compFY = comp.period?.substring(0, 4)
    const compFyNum = parseInt(compFY?.substring(2, 4) || '0', 10)
    if (compFY === fy) return 'completed'
    return compFyNum < viewFyNum ? 'expired' : 'not_done'
  }
}

const COMPLETION_CONFIG = {
  completed: { bg: 'bg-green-50 dark:bg-green-900/20', color: 'text-green-600' },
  not_done: { bg: 'bg-red-50 dark:bg-red-900/20', color: 'text-red-600' },
  expired: { bg: 'bg-amber-50 dark:bg-amber-900/20', color: 'text-amber-600' },
  na: { bg: 'bg-gray-50 dark:bg-gray-800', color: 'text-gray-300' },
}

// Map API venue_id (e.g. TWINSTARDIGGERS) to canonical id matching staff.venueId (diggers)
function toCanonicalVenueId(venueId) {
  if (!venueId) return venueId
  const s = String(venueId).toLowerCase()
  if (s.includes('diggers') || /twinstar.*diggers|tsd/i.test(s)) return 'diggers'
  if (s.includes('leagues') || /twinstar.*leagues/i.test(s)) return 'leagues'
  if (s.includes('golf') || /twinstar.*golf|^tsg.*golf|golf.*demo/i.test(s)) return 'golf'
  if (s.includes('sports') || /twinstar.*sports/i.test(s)) return 'sports'
  return s
}

/* ================================================================
   VENUES HOOK
   ================================================================ */
function useVenues() {
  const [venues, setVenues] = useState([])
  useEffect(() => {
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && j.data.length > 0) {
          setVenues(
            j.data.map((v) => ({
              id: toCanonicalVenueId(v.venue_id),
              name: v.name,
              shortName: v.code || v.name?.split(' ')[0] || v.venue_id,
            }))
          )
        }
      } catch {}
    })()
  }, [])
  return venues
}

/* ================================================================
   MODALS
   ================================================================ */
function ManageCoursesModal({ onClose, courses, onChanged }) {
  const emptyForm = { name: '', provider: '', frequency: 'annual', roles: 'all' }
  const [editingId, setEditingId] = useState(null) // course id, or 'new'
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const startAdd = () => { setEditingId('new'); setForm(emptyForm); setError('') }
  const startEdit = (c) => {
    setEditingId(c.id)
    setForm({ name: c.name || '', provider: c.provider || '', frequency: c.frequency || 'annual', roles: c.roles || 'all' })
    setError('')
  }
  const cancel = () => { setEditingId(null); setError('') }

  const save = async () => {
    if (!form.name.trim()) { setError('Course name is required.'); return }
    setSaving(true)
    setError('')
    try {
      const isNew = editingId === 'new'
      const url = isNew
        ? '/api/training-completions/courses'
        : `/api/training-completions/courses/${encodeURIComponent(editingId)}`
      const r = await authFetch(getLocalApiUrl(url), {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`)
      setEditingId(null)
      onChanged?.()
    } catch (e) {
      setError(e.message || 'Could not save the course')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (c) => {
    if (!window.confirm(`Delete the course "${c.name}"?`)) return
    setError('')
    try {
      const r = await authFetch(getLocalApiUrl(`/api/training-completions/courses/${encodeURIComponent(c.id)}`), { method: 'DELETE' })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`)
      onChanged?.()
    } catch (e) {
      setError(e.message || 'Could not delete the course')
    }
  }

  const formBox = (
    <div className="space-y-3">
      <div>
        <label className="text-[11px] font-semibold text-gray-500 uppercase block mb-1">Course name</label>
        <input className="input-field w-full !text-sm !py-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={255} />
      </div>
      <div>
        <label className="text-[11px] font-semibold text-gray-500 uppercase block mb-1">Provider</label>
        <input className="input-field w-full !text-sm !py-1.5" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] font-semibold text-gray-500 uppercase block mb-1">Frequency</label>
          <select className="select-field w-full !text-sm !py-1.5" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
            {Object.entries(COURSE_FREQUENCY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold text-gray-500 uppercase block mb-1">Applies to</label>
          <select className="select-field w-full !text-sm !py-1.5" value={form.roles} onChange={(e) => setForm({ ...form, roles: e.target.value })}>
            {Object.entries(ROLE_SCOPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button className="btn-secondary !text-sm !px-3 !py-1.5" onClick={cancel} disabled={saving}>Cancel</button>
        <button className="btn-primary !text-sm !px-3 !py-1.5" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save course'}</button>
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-xl max-h-[85vh] overflow-auto p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-psg-teal dark:text-psg-teal-300">Manage Courses</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-3.5">
          Define required AML/CTF training courses, frequency, and applicable roles.
        </div>
        {error && !editingId && <div className="text-sm text-red-600 mb-2">{error}</div>}
        {courses.map((c) => {
          const fr = courseFreqMeta(c.frequency)
          return (
            <div key={c.id} className="flex gap-2.5 items-center p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 mb-1">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{c.name}</div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400">{c.provider || 'No provider'} · {ROLE_SCOPE_LABELS[c.roles] || ROLE_SCOPE_LABELS.all}</div>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${fr.bg} ${fr.color}`}>{fr.label}</span>
              <button className="p-1 hover:bg-white dark:hover:bg-gray-600 rounded" title="Edit" onClick={() => startEdit(c)}><PencilSquareIcon className="w-4 h-4 text-gray-400" /></button>
              <button className="p-1 hover:bg-white dark:hover:bg-gray-600 rounded" title="Delete" onClick={() => remove(c)}><TrashIcon className="w-4 h-4 text-gray-400" /></button>
            </div>
          )
        })}
        <button onClick={startAdd} className="mt-2.5 text-xs font-semibold px-3.5 py-2 rounded-md flex gap-1 items-center border border-psg-teal/30 bg-psg-teal/10 text-psg-teal hover:bg-psg-teal/20 transition-colors">
          <PlusIcon className="w-4 h-4" /> Add Course
        </button>
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="btn-primary !text-sm !px-4 !py-1.5">Done</button>
        </div>
      </div>
      {editingId && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={saving ? undefined : cancel}>
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-bold text-psg-teal dark:text-psg-teal-300">{editingId === 'new' ? 'Add Course' : 'Edit Course'}</h3>
              <button onClick={cancel} disabled={saving} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
            {formBox}
          </div>
        </div>
      )}
    </div>
  )
}

const CSV_HEADERS = ['Staff Name', 'Course Name', 'Completion Date', 'Provider']
const CERT_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']
const CERT_MAX = 8 * 1024 * 1024

function downloadCompletionTemplate() {
  const csv = `${CSV_HEADERS.join(',')}\n`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'training-completion-template.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function parseCsv(text) {
  const rows = []
  let row = []
  let cell = ''
  let quoted = false
  const src = String(text || '').replace(/^\uFEFF/, '')
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++ } else quoted = false
      } else cell += ch
    } else if (ch === '"') quoted = true
    else if (ch === ',') { row.push(cell); cell = '' }
    else if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = '' }
    else if (ch !== '\r') cell += ch
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row) }
  return rows.filter((r) => r.some((c) => String(c).trim()))
}

function parseCompletionDate(value) {
  const s = String(value || '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!m) return null
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const raw = String(reader.result || '')
      resolve(raw.includes(',') ? raw.split(',')[1] : raw)
    }
    reader.onerror = () => reject(new Error('Could not read the certificate'))
    reader.readAsDataURL(file)
  })
}

function RecordCompletionModal({ onClose, onSaved, staff, venueStaff, courses }) {
  const [mode, setMode] = useState('single')
  const [staffId, setStaffId] = useState('')
  const [courseId, setCourseId] = useState('')
  const [completionDate, setCompletionDate] = useState(localIsoDate())
  const [provider, setProvider] = useState('')
  const [bulkStaffIds, setBulkStaffIds] = useState(new Set())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [certificate, setCertificate] = useState(null)
  const [certError, setCertError] = useState('')
  const [importRows, setImportRows] = useState([])
  const [importFileName, setImportFileName] = useState('')
  const [importNote, setImportNote] = useState('')

  const toggleBulkStaff = (id) => {
    setBulkStaffIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const takeCertificate = (file) => {
    setCertError('')
    if (!file) return
    if (file.type && !CERT_TYPES.includes(file.type)) {
      setCertificate(null)
      setCertError('Use a PDF, JPEG, PNG, or WebP certificate.')
      return
    }
    if (file.size > CERT_MAX) {
      setCertificate(null)
      setCertError('Certificate must be 8 MB or smaller.')
      return
    }
    setCertificate(file)
  }

  const handleSave = async () => {
    console.log('🚀 handleSave called, mode:', mode)
    setError('')
    if (mode === 'single') {
      console.log('📝 Single mode data:', { staffId, courseId, completionDate, provider })
      if (!staffId || !courseId || !completionDate) {
        setError('Please select staff, course, and completion date.')
        return
      }
      setSaving(true)
      try {
        const body = {
          staff_id: parseInt(staffId, 10),
          course_id: courseId,
          completion_date: completionDate,
          provider: provider || null,
        }
        if (certificate) {
          body.certificate_name = certificate.name
          body.certificate_mime = certificate.type || 'application/octet-stream'
          body.certificate_base64 = await fileToBase64(certificate)
        }
        const r = await authFetch(getLocalApiUrl('/api/training-completions'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        console.log('📡 Response status:', r.status, 'OK:', r.ok)
        const j = await r.json()
        console.log('📦 Response data:', j)
        if (!r.ok || !j.success) {
          const errorMsg = j.error || `Failed to save (status: ${r.status})`
          console.error('❌ Save failed:', errorMsg, j)
          setError(errorMsg)
          return
        }
        console.log('✅ Save successful!')
        onSaved?.({ completionDate, courseId, staffIds: [parseInt(staffId, 10)] })
        onClose()
      } catch (e) {
        console.error('❌ Exception during save:', e)
        setError(e.message || 'Failed to save')
      } finally {
        setSaving(false)
      }
    } else if (mode === 'bulk') {
      console.log('📝 Bulk mode data:', { courseId, completionDate, provider, staffCount: bulkStaffIds.size })
      if (!courseId || !completionDate || bulkStaffIds.size === 0) {
        setError('Please select course, completion date, and at least one staff member.')
        return
      }
      setSaving(true)
      try {
        let failed = 0
        let succeeded = 0
        console.log(`🌐 Starting bulk save for ${bulkStaffIds.size} staff members...`)
        for (const sid of bulkStaffIds) {
          const r = await authFetch(getLocalApiUrl('/api/training-completions'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              staff_id: parseInt(sid, 10),
              course_id: courseId,
              completion_date: completionDate,
              provider: provider || null,
            }),
          })
          const j = await r.json()
          if (!r.ok || !j.success) {
            failed += 1
            console.error(`❌ Failed for staff_id ${sid}:`, j)
          } else {
            succeeded += 1
            console.log(`✅ Saved for staff_id ${sid}`)
          }
        }
        console.log(`📊 Bulk save complete: ${succeeded} succeeded, ${failed} failed`)
        if (failed > 0) setError(`${failed} of ${bulkStaffIds.size} failed to save`)
        else {
          onSaved?.({ completionDate, courseId, staffIds: [...bulkStaffIds].map((id) => parseInt(id, 10)) })
          onClose()
        }
      } catch (e) {
        console.error('❌ Exception during bulk save:', e)
        setError(e.message || 'Failed to save')
      } finally {
        setSaving(false)
      }
    } else {
      const ready = importRows.filter((r) => r.staffId && r.courseId && r.date)
      const unmatched = importRows.filter((r) => !r.staffId || !r.courseId || !r.date)
      if (!ready.length) {
        setError(importRows.length ? 'No rows matched a staff member, course, and date.' : 'Choose a CSV file first.')
        return
      }
      setSaving(true)
      try {
        let failed = 0
        for (const row of ready) {
          const r = await authFetch(getLocalApiUrl('/api/training-completions'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              staff_id: row.staffId,
              course_id: row.courseId,
              completion_date: row.date,
              provider: row.provider || null,
            }),
          })
          const j = await r.json().catch(() => ({}))
          if (!r.ok || !j.success) failed += 1
        }
        if (failed) setError(`${failed} of ${ready.length} matched rows failed to save.`)
        else if (unmatched.length) setImportNote(`${ready.length} saved. ${unmatched.length} unmatched — review those rows and record them manually.`)
        else {
          onSaved?.({ completionDates: ready.map((row) => row.date) })
          onClose()
        }
        if (!failed && unmatched.length) onSaved?.({ completionDates: ready.map((row) => row.date) })
      } catch (e) {
        setError(e.message || 'Import failed')
      } finally {
        setSaving(false)
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-psg-teal dark:text-psg-teal-300">Record Completion</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-700 rounded-md p-0.5">
          {[
            { key: 'single', label: 'Single Entry' },
            { key: 'bulk', label: 'Bulk Entry' },
            { key: 'import', label: 'CSV Import' },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => {
                if (m.key === mode) return
                setMode(m.key)
                setError('')
                setImportNote('')
                setCertError('')
              }}
              className={`flex-1 text-xs font-medium px-2.5 py-1.5 rounded transition-all ${
                mode === m.key
                  ? 'bg-white dark:bg-gray-600 text-psg-teal dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {mode === 'single' && (
          <>
            <div className="mb-3">
              <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Staff Member</label>
              <select className="select-field" value={staffId} onChange={(e) => setStaffId(e.target.value)}>
                <option value="">Select staff...</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} — {s.role}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Course</label>
              <select className="select-field" value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                <option value="">Select course...</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Completion Date</label>
                <input type="date" className="input-field" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Provider</label>
                <input placeholder="e.g. AUSTRAC eLearning" className="input-field" value={provider} onChange={(e) => setProvider(e.target.value)} />
              </div>
            </div>
            <div className="mb-3.5">
              <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Certificate / Evidence (optional)</label>
              <label
                className="block p-3 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-600 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); takeCertificate(e.dataTransfer.files?.[0]) }}
              >
                <input
                  className="hidden"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
                  onChange={(e) => { takeCertificate(e.target.files?.[0]); e.target.value = '' }}
                />
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {certificate ? `Selected file: ${certificate.name}` : 'Drop certificate or click to upload'}
                </div>
                <div className="text-[11px] text-gray-400 mt-1">PDF, JPEG, PNG, or WebP, up to 8 MB. Optional.</div>
              </label>
              {certificate && (
                <button type="button" className="mt-1 text-xs text-gray-500 underline" onClick={() => setCertificate(null)}>Remove file</button>
              )}
              {certError && <div className="mt-1 text-xs text-red-600">{certError}</div>}
            </div>
          </>
        )}
        {mode === 'bulk' && (
          <>
            <div className="mb-3">
              <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Course</label>
              <select className="select-field" value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                <option value="">Select course...</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Completion Date</label>
                <input type="date" className="input-field" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Provider</label>
                <input placeholder="e.g. AUSTRAC eLearning" className="input-field" value={provider} onChange={(e) => setProvider(e.target.value)} />
              </div>
            </div>
            <div className="mb-3.5">
              <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1.5">Select Staff Who Completed</label>
              <div className="max-h-[180px] overflow-auto rounded-md border border-gray-200 dark:border-gray-600">
                {venueStaff.map((s) => (
                  <label key={s.id} className="flex gap-2 items-center px-2.5 py-1.5 cursor-pointer border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <input type="checkbox" className="rounded" checked={bulkStaffIds.has(s.id)} onChange={() => toggleBulkStaff(s.id)} />
                    <span className="text-sm text-gray-900 dark:text-gray-100">{s.name}</span>
                    <span className="text-[11px] text-gray-400">{s.role}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
        {mode === 'import' && (
          <>
            <div className="flex justify-end mb-2">
              <button type="button" className="text-xs font-semibold text-psg-teal underline" onClick={downloadCompletionTemplate}>
                Download template
              </button>
            </div>
            <label
              className="block p-6 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-600 text-center cursor-pointer mb-3 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                const file = e.dataTransfer.files?.[0]
                if (!file) return
                setImportFileName(file.name || 'uploaded.csv')
                file.text().then((text) => {
                  const parsed = parseCsv(text)
                  const header = (parsed[0] || []).map((h) => h.trim().toLowerCase())
                  const idx = (name) => header.indexOf(name.toLowerCase())
                  const body = header.includes('staff name') ? parsed.slice(1) : parsed
                  const staffCol = idx('staff name') >= 0 ? idx('staff name') : 0
                  const courseCol = idx('course name') >= 0 ? idx('course name') : 1
                  const dateCol = idx('completion date') >= 0 ? idx('completion date') : 2
                  const providerCol = idx('provider') >= 0 ? idx('provider') : 3
                  const next = body.map((cols) => {
                    const staffName = String(cols[staffCol] || '').trim()
                    const courseName = String(cols[courseCol] || '').trim()
                    const person = staff.find((s) => String(s.name || '').trim().toLowerCase() === staffName.toLowerCase())
                    const course = courses.find((c) => String(c.name || '').trim().toLowerCase() === courseName.toLowerCase())
                    return {
                      staffName,
                      courseName,
                      date: parseCompletionDate(cols[dateCol]),
                      provider: String(cols[providerCol] || '').trim(),
                      staffId: person?.id || null,
                      courseId: course?.id || null,
                    }
                  }).filter((r) => r.staffName || r.courseName)
                  setImportRows(next)
                  setImportNote('')
                  setError('')
                }).catch(() => setError('Could not read that CSV file.'))
              }}
            >
              <input
                className="hidden"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (!file) return
                  setImportFileName(file.name || 'uploaded.csv')
                  file.text().then((text) => {
                    const parsed = parseCsv(text)
                    const header = (parsed[0] || []).map((h) => h.trim().toLowerCase())
                    const idx = (name) => header.indexOf(name.toLowerCase())
                    const body = header.includes('staff name') ? parsed.slice(1) : parsed
                    const staffCol = idx('staff name') >= 0 ? idx('staff name') : 0
                    const courseCol = idx('course name') >= 0 ? idx('course name') : 1
                    const dateCol = idx('completion date') >= 0 ? idx('completion date') : 2
                    const providerCol = idx('provider') >= 0 ? idx('provider') : 3
                    const next = body.map((cols) => {
                      const staffName = String(cols[staffCol] || '').trim()
                      const courseName = String(cols[courseCol] || '').trim()
                      const person = staff.find((s) => String(s.name || '').trim().toLowerCase() === staffName.toLowerCase())
                      const course = courses.find((c) => String(c.name || '').trim().toLowerCase() === courseName.toLowerCase())
                      return {
                        staffName,
                        courseName,
                        date: parseCompletionDate(cols[dateCol]),
                        provider: String(cols[providerCol] || '').trim(),
                        staffId: person?.id || null,
                        courseId: course?.id || null,
                      }
                    }).filter((r) => r.staffName || r.courseName)
                    setImportRows(next)
                    setImportNote('')
                    setError('')
                  }).catch(() => setError('Could not read that CSV file.'))
                }}
              />
              <ArrowUpTrayIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {importFileName ? importFileName : 'Drop CSV file or click to upload'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Expected columns: Staff Name, Course Name, Completion Date, Provider</div>
            </label>
            <div className="p-2 rounded-md text-[11px] bg-psg-teal/10 text-psg-teal dark:bg-psg-teal/20 dark:text-psg-teal-200">
              Dates are YYYY-MM-DD or DD/MM/YYYY. Staff and courses are matched by name. Unmatched rows stay listed for manual review and are not saved.
            </div>
            {importRows.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                {importRows.filter((r) => r.staffId && r.courseId && r.date).length} ready,{' '}
                {importRows.filter((r) => !r.staffId || !r.courseId || !r.date).length} need manual review
              </div>
            )}
            {importNote && <div className="mt-2 text-xs text-amber-700">{importNote}</div>}
          </>
        )}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md mt-3">
            <p className="text-sm text-red-600 dark:text-red-400 font-semibold">⚠️ {error}</p>
          </div>
        )}
        <div className="flex gap-2 justify-end mt-3.5">
          <button onClick={onClose} className="btn-secondary" disabled={saving}>Cancel</button>
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : mode === 'import' ? 'Import' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ================================================================
   MAIN
   ================================================================ */
function CorrectCompletionModal({ target, onClose, onSaved }) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    const text = reason.trim()
    if (text.length < 3) {
      setError('Enter a reason for this correction.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const r = await authFetch(getLocalApiUrl(`/api/training-completions/${encodeURIComponent(target.id)}/correct`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: text }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        setError(j.error || 'Could not correct this completion')
        return
      }
      onSaved?.()
    } catch (e) {
      setError(e.message || 'Could not correct this completion')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6 shadow-xl">
        <h2 className="text-lg font-bold text-psg-teal mb-2">Correct completion</h2>
        <p className="text-sm text-gray-700 dark:text-gray-200 m-0">
          {target.staffName} · {target.courseName}
          {target.completionDate ? ` · ${target.completionDate}` : ''}
        </p>
        <p className="text-xs text-gray-500 mt-2 mb-3">
          This takes the completion off the matrix. The original record stays
          {target.hasCertificate ? ', including the certificate' : ''}.
          The cell stays Yes if another valid completion still covers this course.
        </p>
        <label className="text-[11px] font-semibold text-gray-500 uppercase block mb-1">Reason</label>
        <textarea className="input-field w-full" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        {error ? <p className="text-sm text-red-600 mt-2 mb-0">{error}</p> : null}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Correct completion'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ================================================================
   MAIN
   ================================================================ */
export default function TrainingTracker() {
  const { can } = useAuth()
  const canCorrectTraining = can('governance-safe/training', 'manage')
  const venues = useVenues()
  const [venueFilter, setVenueFilter] = useState('all')
  const [trainingPeriods] = useState(() => buildTrainingPeriods())
  const [periodIdx, setPeriodIdx] = useState(() => {
    const periods = buildTrainingPeriods()
    const currentKey = australianFyQuarter().key
    const idx = periods.findIndex((p) => p.key === currentKey)
    return idx >= 0 ? idx : Math.max(0, periods.length - 1)
  })
  const [showManage, setShowManage] = useState(false)
  const [coursesReloadKey, setCoursesReloadKey] = useState(0)
  const [showRecord, setShowRecord] = useState(false)
  const [staff, setStaff] = useState([])
  const [courses, setCourses] = useState([])
  const [coursesLoadError, setCoursesLoadError] = useState('')
  const [completions, setCompletions] = useState({})
  const [correcting, setCorrecting] = useState(null)

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/staff'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data)) {
          setStaff(j.data)
        }
      } catch (_) {}
    }
    loadStaff()
  }, [])

  useEffect(() => {
    const mapCourseRows = (rows) =>
      rows.map((c) => {
        let pc = {}
        if (c.periodic_config != null) {
          if (typeof c.periodic_config === 'object') pc = c.periodic_config
          else if (typeof c.periodic_config === 'string') {
            try {
              pc = JSON.parse(c.periodic_config)
            } catch {
              pc = {}
            }
          }
        }
        return {
          id: c.control_id != null && String(c.control_id).trim() !== '' ? c.control_id : c.id,
          name: c.name,
          shortName: c.short_name || c.name.split(' ').slice(0, 2).join(' '),
          frequency: c.frequency || pc.frequency || 'annual',
          roles: c.roles || pc.roles || 'all',
          provider: c.provider || pc.provider || '',
        }
      })

    const loadCourses = async () => {
      setCoursesLoadError('')
      const urls = [
        getLocalApiUrl('/api/training-completions/courses'),
        getLocalApiUrl('/api/governance/training/courses'),
      ]
      const failures = []
      try {
        for (const url of urls) {
          const r = await authFetch(url)
          let j
          try {
            j = await r.json()
          } catch {
            failures.push(`${r.status}: non-JSON response`)
            continue
          }
          if (r.ok && j.success && Array.isArray(j.data)) {
            setCourses(mapCourseRows(j.data))
            setCoursesLoadError('')
            return
          }
          const hint = j.error || j.message || r.statusText || `HTTP ${r.status}`
          failures.push(`${r.status} ${hint}`)
        }
        setCourses([])
        setCoursesLoadError(
          `Could not load the training course list (${failures.join(' · ')}). Check that the API is restarted, tenant DB has governance migration 019, and your session matches this organization.`,
        )
      } catch (e) {
        setCourses([])
        setCoursesLoadError(`Could not load training courses: ${e.message || 'network error'}`)
      }
    }
    loadCourses()
  }, [coursesReloadKey])

  const openCertificate = async (completionId) => {
    const r = await authFetch(getLocalApiUrl(`/api/training-completions/${encodeURIComponent(completionId)}/certificate`))
    if (!r.ok) return
    const blob = await r.blob()
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  }

  const loadCompletions = async () => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/training-completions'))
      const j = await r.json()
      if (j.success && Array.isArray(j.data)) {
        const completionsMap = {}
        const sorted = [...j.data].sort((a, b) => String(b.completion_date || '').localeCompare(String(a.completion_date || '')))
        sorted.forEach(c => {
          const periodKey = periodKeyFromYmd(c.completion_date)
          if (!periodKey) return
          const courseIds = [c.course_id, c.course_control_id, c.course_db_id]
            .map((id) => (id == null ? '' : String(id).trim()))
            .filter(Boolean)
          const staffIds = [c.staff_id].map((id) => (id == null ? '' : String(id).trim())).filter(Boolean)
          const row = { completion_date: c.completion_date, expiry_date: c.expiry_date, period: periodKey, id: c.id, certificate_url: c.certificate_url || null }
          for (const sid of staffIds) {
            for (const cid of [...new Set(courseIds)]) {
              const baseKey = `${sid}_${cid}`
              const periodSpecificKey = `${sid}_${cid}_${periodKey}`
              // Newest date first — an older row must not replace the current FY completion
              if (!completionsMap[baseKey]) completionsMap[baseKey] = row
              if (!completionsMap[periodSpecificKey]) completionsMap[periodSpecificKey] = row
            }
          }
        })
        setCompletions(completionsMap)
      }
    } catch (e) {
      console.error('Failed to load training completions:', e)
    }
  }

  useEffect(() => {
    loadCompletions()
  }, [])

  const trainingStaff = useMemo(() => staff.map((s, i) => ({
    id: s.id || i + 1,
    venueId: s.venue_id ? toCanonicalVenueId(s.venue_id) : '',
    venueName: s.venue_name || '',
    level: s.level || '',
    name: s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim(),
    role: s.role || s.position || '',
    roleKeys: (s.role_keys || ['all'])
  })), [staff])

  const period = trainingPeriods[periodIdx] || trainingPeriods[0] || { key: '', label: '', range: '' }

  const filteredStaff = useMemo(() => {
    if (venueFilter === 'all') return trainingStaff
    return trainingStaff.filter((s) => s.venueId === venueFilter)
  }, [venueFilter, trainingStaff])

  const courseStats = useMemo(() => {
    return courses.map((c) => {
      const applicable = filteredStaff.filter((s) => isCourseApplicable(c, s))
      const completed = applicable.filter((s) => getCompletionStatus(c, s, period.key, completions) === 'completed')
      const pct = applicable.length > 0 ? Math.round((completed.length / applicable.length) * 100) : 100
      return { ...c, applicable: applicable.length, completed: completed.length, pct }
    })
  }, [filteredStaff, period.key, courses, completions])

  const overallPct = useMemo(() => {
    let totalApplicable = 0
    let totalCompleted = 0
    courseStats.forEach((cs) => {
      totalApplicable += cs.applicable
      totalCompleted += cs.completed
    })
    return totalApplicable > 0 ? Math.round((totalCompleted / totalApplicable) * 100) : 100
  }, [courseStats])

  const venueComplianceStats = useMemo(() => {
    return venues.map((venue) => {
      const venueStaff = trainingStaff.filter((s) => s.venueId === venue.id)
      let totalApplicable = 0
      let totalCompleted = 0
      courses.forEach((c) => {
        const applicable = venueStaff.filter((s) => isCourseApplicable(c, s))
        const completed = applicable.filter((s) => getCompletionStatus(c, s, period.key, completions) === 'completed')
        totalApplicable += applicable.length
        totalCompleted += completed.length
      })
      const pct = totalApplicable > 0 ? Math.round((totalCompleted / totalApplicable) * 100) : null
      return {
        ...venue,
        staffCount: venueStaff.length,
        totalApplicable,
        totalCompleted,
        pct,
      }
    })
  }, [venues, trainingStaff, courses, period.key, completions])

  function StatusCell({ status }) {
    const cfg = COMPLETION_CONFIG[status] || COMPLETION_CONFIG.na
    if (status === 'na') {
      return <span className="text-[11px] font-bold text-gray-400 tabular-nums">—</span>
    }
    const label = status === 'completed' ? 'Yes' : status === 'expired' ? 'Exp' : 'No'
    return (
      <span className={`inline-flex min-w-[2.25rem] justify-center text-[11px] font-extrabold tabular-nums tracking-tight ${cfg.color}`}>
        {label}
      </span>
    )
  }

  return (
    <div className="max-w-7xl mx-auto pb-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex gap-2 items-center">
            <BookOpenIcon className="w-6 h-6 text-psg-teal" /> Training Register
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            ML/TF risk awareness training — course compliance by period
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={venueFilter}
            onChange={(e) => setVenueFilter(e.target.value)}
            className="select-field w-auto min-w-[200px] text-sm"
          >
            <option value="all">All Venues</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowManage(true)}
            className="btn-secondary text-sm py-2 flex gap-1 items-center"
          >
            <Cog6ToothIcon className="w-4 h-4" /> Courses
          </button>
          <button
            type="button"
            onClick={() => setShowRecord(true)}
            className="btn-primary text-sm py-2 flex gap-1 items-center"
          >
            <PlusIcon className="w-4 h-4" /> Record Completion
          </button>
        </div>
      </div>

      {coursesLoadError && (
        <div
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-100"
          role="alert"
        >
          {coursesLoadError}
        </div>
      )}

      {/* Period bar + overall compliance - top most row */}
      <div className="card flex flex-col sm:flex-row mb-4 overflow-hidden p-0">
        <div className="px-4 py-3 flex items-center gap-2.5 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700">
          <button
            type="button"
            disabled={periodIdx <= 0}
            onClick={() => setPeriodIdx((i) => Math.max(0, i - 1))}
            className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${periodIdx <= 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
          >
            <ChevronLeftIcon className="w-5 h-5 text-psg-teal" />
          </button>
          <div className="text-center min-w-[110px]">
            <div className="text-base font-extrabold text-psg-teal dark:text-psg-teal-300">{period.label}</div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">{period.range}</div>
          </div>
          <button
            type="button"
            disabled={periodIdx >= trainingPeriods.length - 1}
            onClick={() => setPeriodIdx((i) => Math.min(trainingPeriods.length - 1, i + 1))}
            className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${periodIdx >= trainingPeriods.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
          >
            <ChevronRightIcon className="w-5 h-5 text-psg-teal" />
          </button>
        </div>
        <div
          className={`px-5 py-3 flex items-center justify-center min-w-[100px] ${
            overallPct >= 90 ? 'bg-green-50 dark:bg-green-900/20' : overallPct >= 70 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-red-50 dark:bg-red-900/20'
          }`}
        >
          <div className="text-center">
            <div
              className={`text-[28px] font-extrabold leading-none ${
                overallPct >= 90 ? 'text-green-600' : overallPct >= 70 ? 'text-amber-600' : 'text-red-600'
              }`}
            >
              {overallPct}%
            </div>
            <div
              className={`text-[10px] font-semibold mt-0.5 ${
                overallPct >= 90 ? 'text-green-600' : overallPct >= 70 ? 'text-amber-600' : 'text-red-600'
              }`}
            >
              Overall
            </div>
          </div>
        </div>
        <div className="flex-1 px-5 py-3 flex flex-wrap gap-5 items-center">
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase">Courses</div>
            <div className="text-lg font-extrabold text-gray-900 dark:text-gray-100">{courses.length}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase">Staff</div>
            <div className="text-lg font-extrabold text-gray-900 dark:text-gray-100">{filteredStaff.length}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase">Fully Compliant</div>
            <div className="text-lg font-extrabold text-green-600">
              {courseStats.filter((c) => c.pct === 100).length}/{courses.length}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase">Gaps</div>
            <div
              className={`text-lg font-extrabold ${
                courseStats.some((c) => c.pct < 100) ? 'text-red-600' : 'text-green-600'
              }`}
            >
              {courseStats.reduce((a, c) => a + (c.applicable - c.completed), 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Venue compliance cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
        <button
          type="button"
          onClick={() => setVenueFilter('all')}
          className={`card p-4 text-left transition-all hover:ring-2 hover:ring-psg-teal/40 ${
            venueFilter === 'all' ? 'ring-2 ring-psg-teal bg-psg-teal/5' : ''
          }`}
        >
          <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            All Venues
          </div>
          <div
            className={`text-2xl font-extrabold mt-0.5 ${
              overallPct >= 90 ? 'text-green-600' : overallPct >= 70 ? 'text-amber-600' : 'text-red-600'
            }`}
          >
            {overallPct}%
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {venueComplianceStats.reduce((a, v) => a + v.totalCompleted, 0)}/{venueComplianceStats.reduce((a, v) => a + v.totalApplicable, 0)} · {trainingStaff.length} staff
          </div>
        </button>
        {venueComplianceStats.map((v) => (
          <button
            key={v.id}
            type="button"
            onClick={() => setVenueFilter(venueFilter === v.id ? 'all' : v.id)}
            className={`card p-4 text-left transition-all hover:ring-2 hover:ring-psg-teal/40 ${
              venueFilter === v.id ? 'ring-2 ring-psg-teal bg-psg-teal/5' : ''
            }`}
          >
            <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate">
              {v.shortName || v.name}
            </div>
            <div
              className={`text-2xl font-extrabold mt-0.5 ${
                v.pct == null ? 'text-gray-400' : v.pct >= 90 ? 'text-green-600' : v.pct >= 70 ? 'text-amber-600' : 'text-red-600'
              }`}
            >
              {v.pct != null ? `${v.pct}%` : '—'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {v.totalCompleted}/{v.totalApplicable} · {v.staffCount} staff
            </div>
          </button>
        ))}
      </div>

      {/* Course compliance cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {courseStats.map((c) => {
          const fr = courseFreqMeta(c.frequency)
          return (
            <div key={c.id} className="card p-4">
              <div className="flex gap-1.5 items-center mb-1.5">
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${fr.bg} ${fr.color}`}>{fr.label}</span>
                <span className="text-[9px] text-gray-400">{ROLE_SCOPE_LABELS[c.roles] || ROLE_SCOPE_LABELS.all}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <div className="text-[13px] font-bold leading-tight text-gray-900 dark:text-gray-100 min-w-0 flex-1">{c.name}</div>
                <div
                  className={`text-lg font-extrabold flex-shrink-0 ${
                    c.pct === 100 ? 'text-green-600' : c.pct >= 70 ? 'text-amber-600' : 'text-red-600'
                  }`}
                >
                  {c.completed}/{c.applicable}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Staff × Course matrix (scroll horizontally for many courses; Yes/No/Exp in cells) */}
      <div className="card !p-0 overflow-visible">
        <div className="overflow-x-auto overscroll-x-contain rounded-lg">
          <table className="w-max min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-900/80">
                <th
                  scope="col"
                  className="text-left px-3.5 py-2.5 text-[11px] font-semibold text-gray-500 dark:text-gray-400 min-w-[200px] sticky left-0 z-30 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur-sm shadow-[6px_0_12px_-8px_rgba(0,0,0,0.25)]"
                >
                  Staff Member
                </th>
                {venueFilter === 'all' && (
                  <th
                    scope="col"
                    className="text-left px-3.5 py-2.5 text-[11px] font-semibold text-gray-500 dark:text-gray-400 min-w-[140px] sticky left-[200px] z-30 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur-sm shadow-[6px_0_12px_-8px_rgba(0,0,0,0.25)]"
                  >
                    Venue
                  </th>
                )}
                {courses.map((c) => {
                  const fr = courseFreqMeta(c.frequency)
                  return (
                    <th
                      key={c.id}
                      scope="col"
                      className="px-2 py-2 text-center border-l border-gray-200 dark:border-gray-600 min-w-[76px] max-w-[100px]"
                    >
                      <div className="text-[10px] font-bold leading-tight text-gray-900 dark:text-gray-100 line-clamp-2">
                        {c.shortName}
                      </div>
                      <div className="text-[8px] text-gray-500 dark:text-gray-400 mt-0.5 whitespace-nowrap">{fr.label}</div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {filteredStaff.map((s, si) => {
                const rowBg =
                  si % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/80 dark:bg-gray-800/80'
                return (
                  <tr key={s.id} className={`border-b border-gray-100 dark:border-gray-700 ${rowBg}`}>
                    <td
                      className={`px-3.5 py-2 min-w-[200px] sticky left-0 z-20 shadow-[6px_0_12px_-8px_rgba(0,0,0,0.18)] ${rowBg}`}
                    >
                      <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{s.name}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">{s.role}</div>
                    </td>
                    {venueFilter === 'all' && (
                      <td
                        className={`px-3.5 py-2 min-w-[140px] sticky left-[200px] z-20 shadow-[6px_0_12px_-8px_rgba(0,0,0,0.18)] ${rowBg}`}
                      >
                        <span className="text-xs font-medium text-psg-teal px-2 py-0.5 rounded bg-psg-teal/10">
                          {s.level === 'group' || !s.venueId
                            ? 'Group'
                            : (venues.find((v) => v.id === s.venueId)?.name || s.venueName || s.venueId)}
                        </span>
                      </td>
                    )}
                    {courses.map((c) => {
                      const status = getCompletionStatus(c, s, period.key, completions)
                      const cfg = COMPLETION_CONFIG[status]
                      const certKey = c.frequency === 'quarterly' ? `${s.id}_${c.id}_${period.key}` : `${s.id}_${c.id}`
                      const cert = completions[certKey]
                      return (
                        <td
                          key={c.id}
                          className={`px-1.5 py-2.5 text-center align-middle border-l border-gray-100 dark:border-gray-700 min-w-[76px] ${cfg.bg}`}
                        >
                          <StatusCell status={status} />
                          {status === 'completed' && canCorrectTraining && cert?.id ? (
                            <button
                              type="button"
                              className="block mx-auto mt-1 text-[10px] text-amber-800 underline"
                              onClick={() => setCorrecting({
                                id: cert.id,
                                staffName: s.name,
                                courseName: c.shortName || c.name,
                                completionDate: String(cert.completion_date || '').slice(0, 10),
                                hasCertificate: !!cert.certificate_url,
                              })}
                            >
                              Correct
                            </button>
                          ) : null}
                          {cert?.certificate_url && cert.id ? (
                            <button type="button" className="block mx-auto mt-1 text-[10px] text-psg-teal underline" onClick={() => openCertificate(cert.id)}>
                              Certificate
                            </button>
                          ) : null}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {courses.length === 0 && (
          <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">
            No training courses found. Ensure governance training courses are seeded and you have permission to view them.
          </div>
        )}
        {filteredStaff.length === 0 && (
          <div className="p-12 text-center">
            <BookOpenIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 dark:text-gray-400">No staff found for the selected venue</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3.5 py-2.5 text-[11px] text-gray-500 dark:text-gray-400">
        <span className="flex gap-1 items-center">
          <span className="text-[11px] font-extrabold text-green-600">Yes</span> Completed
        </span>
        <span className="flex gap-1 items-center">
          <span className="text-[11px] font-extrabold text-amber-600">Exp</span> Expired
        </span>
        <span className="flex gap-1 items-center">
          <span className="text-[11px] font-extrabold text-red-600">No</span> Not done
        </span>
        <span>— Not applicable</span>
        <div className="flex-1" />
        <span>AML/CTF Act s81 · AUSTRAC Guide p35</span>
      </div>

      {showManage && <ManageCoursesModal onClose={() => setShowManage(false)} courses={courses} onChanged={() => setCoursesReloadKey((k) => k + 1)} />}
      {showRecord && (
        <RecordCompletionModal
          onClose={() => setShowRecord(false)}
          onSaved={(info) => {
            const dates = [info?.completionDate, ...(info?.completionDates || [])].filter(Boolean)
            const keys = dates.map((d) => periodKeyFromYmd(d)).filter(Boolean)
            if (keys.length) {
              const exact = trainingPeriods.findIndex((p) => keys.includes(p.key))
              if (exact >= 0) setPeriodIdx(exact)
            }
            loadCompletions()
          }}
          staff={trainingStaff}
          venueStaff={filteredStaff}
          courses={courses}
        />
      )}
      {correcting && (
        <CorrectCompletionModal
          target={correcting}
          onClose={() => setCorrecting(null)}
          onSaved={() => {
            setCorrecting(null)
            loadCompletions()
          }}
        />
      )}
    </div>
  )
}
